import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader, Save, Upload, X, Bed, Bath, Maximize, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORIENTATIONS = ['Norte', 'Sur', 'Este', 'Oeste', 'Noreste', 'Noroeste', 'Sureste', 'Suroeste'];

export const EditProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, dataVersion } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [property, setProperty] = useState(null);
  
  const [formData, setFormData] = useState({
    // Básicos
    title: '',
    description: '',
    property_type: 'apartamento',
    listing_type: 'venta',
    price: '',
    purchase_price: '',
    // Características
    bedrooms: '',
    bathrooms: '',
    area: '',
    plot_area: '',
    floor: '',
    total_floors: '',
    orientations: [],
    // Ubicación
    street_name: '',
    street_number: '',
    province: '',
    city: '',
    postal_code: '',
    // Antigüedad
    year_built: '',
    renovation_year: '',
    condition: 'buen_estado',
    energy_cert: 'en_tramite',
    // Extras
    has_elevator: false,
    has_parking: false,
    parking_included: false,
    parking_price: '',
    has_pool: false,
    has_garden: false,
    has_terrace: false,
    terrace_area: '',
    has_storage: false,
    has_ac: false,
    has_heating: false,
    heating_type: '',
    is_furnished: false,
    // Financiero
    community_fee: '',
    ibi_annual: '',
    rental_estimate: ''
  });
  
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  useEffect(() => {
    loadProperty();
  }, [id, dataVersion]);

  const loadProperty = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/properties/${id}?t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const prop = response.data;
      
      if (prop.user_id !== user.user_id) {
        toast.error('No tienes permisos para editar esta propiedad');
        navigate('/my-properties');
        return;
      }

      setProperty(prop);
      
      // Parsear extended_data si existe
      const ext = prop.extended_data || {};
      const orientationsArray = ext.orientation ? ext.orientation.split(', ') : [];
      
      setFormData({
        title: prop.title || '',
        description: prop.description || '',
        property_type: prop.property_type || 'apartamento',
        listing_type: prop.listing_type || 'venta',
        price: prop.price || '',
        purchase_price: prop.purchase_price || ext.purchase_price || '',
        bedrooms: prop.bedrooms || '',
        bathrooms: prop.bathrooms || '',
        area: prop.area || '',
        plot_area: ext.plot_area || '',
        floor: ext.floor || '',
        total_floors: ext.total_floors || '',
        orientations: orientationsArray,
        street_name: '', // No guardamos esto por privacidad
        street_number: '',
        province: prop.province || '',
        city: prop.city || '',
        postal_code: ext.postal_code || '',
        year_built: ext.year_built || '',
        renovation_year: ext.renovation_year || '',
        condition: ext.condition || 'buen_estado',
        energy_cert: ext.energy_cert || 'en_tramite',
        has_elevator: ext.has_elevator || false,
        has_parking: ext.has_parking || false,
        parking_included: ext.parking_included || false,
        parking_price: ext.parking_price || '',
        has_pool: ext.has_pool || false,
        has_garden: ext.has_garden || false,
        has_terrace: ext.has_terrace || false,
        terrace_area: ext.terrace_area || '',
        has_storage: ext.has_storage || false,
        has_ac: ext.has_ac || false,
        has_heating: ext.has_heating || false,
        heating_type: ext.heating_type || '',
        is_furnished: ext.is_furnished || false,
        community_fee: ext.community_fee || '',
        ibi_annual: ext.ibi_annual || '',
        rental_estimate: ext.rental_estimate || ''
      });
      
      // Cargar imágenes existentes
      if (prop.images && prop.images.length > 0) {
        const imageUrls = prop.images.map(img => {
          const url = typeof img === 'object' ? img.url : img;
          return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
        });
        setExistingImages(imageUrls);
      }
    } catch (error) {
      console.error('Error loading property:', error);
      toast.error('Error al cargar la propiedad');
      navigate('/my-properties');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (existingImages.length + newImages.length + files.length > 40) {
      toast.error('Máximo 40 imágenes permitidas');
      return;
    }
    setNewImages([...newImages, ...files]);
  };

  const removeExistingImage = (index) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewImages(newImages.filter((_, i) => i !== index));
  };

  const toggleOrientation = (orientation) => {
    if (formData.orientations.includes(orientation)) {
      setFormData({...formData, orientations: formData.orientations.filter(o => o !== orientation)});
    } else {
      setFormData({...formData, orientations: [...formData.orientations, orientation]});
    }
  };

  const handleSave = async () => {
    if (saving) return;
    
    setSaving(true);
    try {
      let uploadedImageUrls = [];
      
      // 1. Subir nuevas imágenes primero (si hay)
      if (newImages.length > 0) {
        toast.info(`Subiendo ${newImages.length} imágenes...`);
        
        for (let i = 0; i < newImages.length; i++) {
          const file = newImages[i];
          const imageFormData = new FormData();
          imageFormData.append('file', file);
          imageFormData.append('property_id', id);
          imageFormData.append('category', 'general');
          
          const response = await axios.post(`${API}/upload/property-image`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          uploadedImageUrls.push({
            url: response.data.url,
            category: 'general'
          });
          
          toast.info(`Imagen ${i + 1}/${newImages.length} subida`);
        }
        
        toast.success('Todas las imágenes subidas correctamente');
      }

      // 2. Convertir existingImages a formato DB
      const existingImagesForDB = existingImages.map(url => ({
        url: url.includes(BACKEND_URL) ? url.replace(BACKEND_URL, '') : url,
        category: 'general'
      }));
      
      // 3. Combinar imágenes
      const finalImages = [...existingImagesForDB, ...uploadedImageUrls];

      // 4. Preparar datos para actualizar
      const updateData = {
        title: formData.title,
        description: formData.description,
        property_type: formData.property_type,
        listing_type: formData.listing_type,
        price: parseFloat(formData.price) || 0,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        area: parseFloat(formData.area) || 0,
        province: formData.province,
        city: formData.city,
        images: finalImages,
        extended_data: {
          plot_area: parseFloat(formData.plot_area) || null,
          floor: parseInt(formData.floor) || null,
          total_floors: parseInt(formData.total_floors) || null,
          orientation: formData.orientations.length > 0 ? formData.orientations.join(', ') : null,
          year_built: parseInt(formData.year_built) || null,
          renovation_year: parseInt(formData.renovation_year) || null,
          condition: formData.condition,
          energy_cert: formData.energy_cert,
          postal_code: formData.postal_code,
          has_elevator: formData.has_elevator,
          has_parking: formData.has_parking,
          parking_included: formData.parking_included,
          parking_price: parseFloat(formData.parking_price) || null,
          has_pool: formData.has_pool,
          has_garden: formData.has_garden,
          has_terrace: formData.has_terrace,
          terrace_area: parseFloat(formData.terrace_area) || null,
          has_storage: formData.has_storage,
          has_ac: formData.has_ac,
          has_heating: formData.has_heating,
          heating_type: formData.heating_type || null,
          is_furnished: formData.is_furnished,
          community_fee: parseFloat(formData.community_fee) || null,
          ibi_annual: parseFloat(formData.ibi_annual) || null,
          rental_estimate: parseFloat(formData.rental_estimate) || null
        }
      };

      await axios.put(`${API}/properties/${id}`, updateData);

      toast.success('¡Propiedad actualizada correctamente!');
      setTimeout(() => navigate('/my-properties'), 1000);
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error(`Error: ${error.response?.data?.detail || 'No se pudieron guardar los cambios'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader size={32} className="text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-8">
      <div className="max-w-5xl mx-auto pt-8">
        <button 
          onClick={() => navigate('/my-properties')} 
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm">Volver a Mis Propiedades</span>
        </button>

        <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Editar Propiedad</h1>
          <p className="text-gray-400 text-sm mb-8">
            Actualiza todos los detalles de tu propiedad
          </p>
          
          {/* SECCIÓN 1: INFORMACIÓN BÁSICA */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm">1</div>
              Información Básica
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Título del anuncio</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  placeholder="Ej: Piso céntrico con vistas al mar"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={5}
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  placeholder="Describe las características de la propiedad..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Tipo de propiedad</label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="apartamento">Apartamento</option>
                    <option value="casa">Casa</option>
                    <option value="chalet">Chalet</option>
                    <option value="atico">Ático</option>
                    <option value="duplex">Dúplex</option>
                    <option value="estudio">Estudio</option>
                    <option value="loft">Loft</option>
                    <option value="finca">Finca</option>
                    <option value="oficina">Oficina</option>
                    <option value="local">Local Comercial</option>
                    <option value="parking">Plaza de Parking</option>
                    <option value="terreno">Terreno</option>
                  </select>
                </div>

                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Operación</label>
                  <select
                    value={formData.listing_type}
                    onChange={(e) => setFormData({...formData, listing_type: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="venta">Venta</option>
                    <option value="alquiler">Alquiler</option>
                    <option value="alquiler_vacacional">Alquiler Vacacional</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Precio de Venta/Alquiler (€)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Precio de Compra (€)</label>
                  <input
                    type="number"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                    placeholder="Para calcular plusvalía"
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Necesario para cálculo de plusvalía municipal</p>
                </div>
              </div>
            </div>
          </div>

          {/* SECCIÓN 2: CARACTERÍSTICAS */}
          <div className="mb-8 border-t border-white/10 pt-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm">2</div>
              Características
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block flex items-center gap-2">
                    <Maximize size={16} />
                    Área (m²)
                  </label>
                  <input
                    type="number"
                    value={formData.area}
                    onChange={(e) => setFormData({...formData, area: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block flex items-center gap-2">
                    <Bed size={16} />
                    Habitaciones
                  </label>
                  <input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block flex items-center gap-2">
                    <Bath size={16} />
                    Baños
                  </label>
                  <input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Planta</label>
                  <input
                    type="number"
                    value={formData.floor}
                    onChange={(e) => setFormData({...formData, floor: e.target.value})}
                    placeholder="0 = Bajo"
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Total Plantas</label>
                  <input
                    type="number"
                    value={formData.total_floors}
                    onChange={(e) => setFormData({...formData, total_floors: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Área Parcela (m²)</label>
                  <input
                    type="number"
                    value={formData.plot_area}
                    onChange={(e) => setFormData({...formData, plot_area: e.target.value})}
                    placeholder="Si aplica"
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Orientación</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {ORIENTATIONS.map(orientation => (
                    <button
                      key={orientation}
                      type="button"
                      onClick={() => toggleOrientation(orientation)}
                      className={`p-2 rounded-lg border text-sm transition-all ${
                        formData.orientations.includes(orientation)
                          ? 'bg-purple-500/20 border-purple-500 text-purple-400'
                          : 'bg-gray-800 border-white/10 text-gray-400 hover:border-purple-500/50'
                      }`}
                    >
                      {orientation}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Año Construcción</label>
                  <input
                    type="number"
                    value={formData.year_built}
                    onChange={(e) => setFormData({...formData, year_built: e.target.value})}
                    placeholder="1990"
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Año Reforma</label>
                  <input
                    type="number"
                    value={formData.renovation_year}
                    onChange={(e) => setFormData({...formData, renovation_year: e.target.value})}
                    placeholder="2020"
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm font-medium mb-2 block">Estado</label>
                  <select
                    value={formData.condition}
                    onChange={(e) => setFormData({...formData, condition: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="nuevo">Nuevo</option>
                    <option value="buen_estado">Buen Estado</option>
                    <option value="reformar">A Reformar</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Certificado Energético</label>
                <select
                  value={formData.energy_cert}
                  onChange={(e) => setFormData({...formData, energy_cert: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="en_tramite">En Trámite</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                  <option value="E">E</option>
                  <option value="F">F</option>
                  <option value="G">G</option>
                  <option value="exento">Exento</option>
                </select>
              </div>
            </div>
          </div>

          {/* SECCIÓN 3: EXTRAS Y ACCESORIOS */}
          <div className="mb-8 border-t border-white/10 pt-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm">3</div>
              Extras y Accesorios
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_elevator}
                  onChange={(e) => setFormData({...formData, has_elevator: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-gray-300 text-sm">Ascensor</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_pool}
                  onChange={(e) => setFormData({...formData, has_pool: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-gray-300 text-sm">Piscina</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_garden}
                  onChange={(e) => setFormData({...formData, has_garden: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-gray-300 text-sm">Jardín</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_storage}
                  onChange={(e) => setFormData({...formData, has_storage: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-gray-300 text-sm">Trastero</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_ac}
                  onChange={(e) => setFormData({...formData, has_ac: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-gray-300 text-sm">Aire Acondicionado</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_furnished}
                  onChange={(e) => setFormData({...formData, is_furnished: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-gray-300 text-sm">Amueblado</span>
              </label>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={formData.has_terrace}
                    onChange={(e) => setFormData({...formData, has_terrace: e.target.checked})}
                    className="w-5 h-5"
                  />
                  <span className="text-gray-300 text-sm">Terraza</span>
                </label>
                {formData.has_terrace && (
                  <input
                    type="number"
                    value={formData.terrace_area}
                    onChange={(e) => setFormData({...formData, terrace_area: e.target.value})}
                    placeholder="Área de terraza (m²)"
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  />
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={formData.has_heating}
                    onChange={(e) => setFormData({...formData, has_heating: e.target.checked})}
                    className="w-5 h-5"
                  />
                  <span className="text-gray-300 text-sm">Calefacción</span>
                </label>
                {formData.has_heating && (
                  <select
                    value={formData.heating_type}
                    onChange={(e) => setFormData({...formData, heating_type: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Tipo de calefacción</option>
                    <option value="gas_natural">Gas Natural</option>
                    <option value="electrica">Eléctrica</option>
                    <option value="gasoil">Gasoil</option>
                    <option value="aerotermia">Aerotermia</option>
                    <option value="central">Central</option>
                    <option value="individual">Individual</option>
                  </select>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={formData.has_parking}
                    onChange={(e) => setFormData({...formData, has_parking: e.target.checked})}
                    className="w-5 h-5"
                  />
                  <span className="text-gray-300 text-sm">Parking</span>
                </label>
                {formData.has_parking && (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.parking_included}
                        onChange={(e) => setFormData({...formData, parking_included: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-400 text-sm">Parking incluido en el precio</span>
                    </label>
                    {!formData.parking_included && (
                      <input
                        type="number"
                        value={formData.parking_price}
                        onChange={(e) => setFormData({...formData, parking_price: e.target.value})}
                        placeholder="Precio adicional del parking (€)"
                        className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECCIÓN 4: DATOS FINANCIEROS */}
          <div className="mb-8 border-t border-white/10 pt-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm">4</div>
              Datos Financieros
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Gastos de Comunidad (€/mes)</label>
                <input
                  type="number"
                  value={formData.community_fee}
                  onChange={(e) => setFormData({...formData, community_fee: e.target.value})}
                  placeholder="80"
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">IBI Anual (€)</label>
                <input
                  type="number"
                  value={formData.ibi_annual}
                  onChange={(e) => setFormData({...formData, ibi_annual: e.target.value})}
                  placeholder="600"
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Estimación Alquiler (€/mes)</label>
                <input
                  type="number"
                  value={formData.rental_estimate}
                  onChange={(e) => setFormData({...formData, rental_estimate: e.target.value})}
                  placeholder="1200"
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">Para calcular rentabilidad</p>
              </div>
            </div>
          </div>

          {/* SECCIÓN 5: UBICACIÓN */}
          <div className="mb-8 border-t border-white/10 pt-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm">5</div>
              <MapPin size={20} />
              Ubicación
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Provincia</label>
                <input
                  type="text"
                  value={formData.province}
                  onChange={(e) => setFormData({...formData, province: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Ciudad/Localidad</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm font-medium mb-2 block">Código Postal</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  placeholder="28001"
                  className="w-full bg-gray-800 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* SECCIÓN 6: IMÁGENES */}
          <div className="mb-8 border-t border-white/10 pt-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm">6</div>
              Imágenes
            </h2>

            {/* Imágenes Existentes */}
            {existingImages.length > 0 && (
              <div className="mb-6">
                <label className="text-gray-400 text-sm font-medium mb-2 block">Imágenes Actuales ({existingImages.length})</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {existingImages.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={img} 
                        alt={`Imagen ${idx + 1}`} 
                        className="w-full h-32 object-cover rounded-lg border border-white/10"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(idx)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nuevas Imágenes */}
            {newImages.length > 0 && (
              <div className="mb-6">
                <label className="text-gray-400 text-sm font-medium mb-2 block">Nuevas Imágenes ({newImages.length})</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {newImages.map((file, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`Nueva ${idx + 1}`} 
                        className="w-full h-32 object-cover rounded-lg border border-white/10"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewImage(idx)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subir Más Imágenes */}
            <div>
              <label className="text-gray-400 text-sm font-medium mb-2 block">
                Agregar Imágenes ({existingImages.length + newImages.length}/40)
              </label>
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-purple-500 transition">
                <Upload size={40} className="text-gray-500 mb-2" />
                <span className="text-sm text-gray-400">Click para subir imágenes</span>
                <span className="text-xs text-gray-500 mt-1">Máximo 40 imágenes</span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex flex-col sm:flex-row gap-3 border-t border-white/10 pt-8">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all disabled:opacity-50 text-black"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}
            >
              {saving ? <Loader size={18} className="animate-spin" /> : <Save size={18} />}
              {saving ? 'Guardando Cambios...' : 'Guardar Cambios'}
            </button>
            <button
              onClick={() => navigate('/my-properties')}
              className="px-6 py-3 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 transition-all"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
